import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { Input } from '@/components/ui/Input/Input';
import { Select } from '@/components/ui/Select/Select';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { HStack, VStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { SettingsService } from '@/services/SettingsService';
import { HardDrive, Loader2, PlugZap } from 'lucide-react';

const PROVIDER_OPTIONS = [
  { value: 'r2', label: 'Cloudflare R2' },
  { value: 's3', label: 'Amazon S3' },
  { value: 'hetzner', label: 'Hetzner Object Storage' },
  { value: 'minio', label: 'MinIO' },
];

export function StorageSettingsCard() {
  const queryClient = useQueryClient();
  const { success, error: notifyError } = useNotificationStore();
  const service = new SettingsService();

  const [provider, setProvider] = useState<string>('');
  const [endpoint, setEndpoint] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [bucket, setBucket] = useState('');
  const [publicBaseUrl, setPublicBaseUrl] = useState('');
  const [region, setRegion] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings', 'storage'],
    queryFn: () => service.getStorageSettings(),
  });

  // Populate form from fetched settings
  useEffect(() => {
    if (settings) {
      setProvider(settings.provider || '');
      setEndpoint(settings.endpoint || '');
      setAccessKeyId(''); // Don't show masked value as editable
      setSecretAccessKey(''); // Don't show masked value as editable
      setBucket(settings.bucket || '');
      setPublicBaseUrl(settings.public_base_url || '');
      setRegion(settings.region || '');
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: Parameters<typeof service.updateStorageSettings>[0]) =>
      service.updateStorageSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings', 'storage'] });
      success('Storage settings saved');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to save storage settings');
    },
  });

  const testMutation = useMutation({
    mutationFn: () => service.testStorage(),
    onSuccess: (data) => {
      success(data.message || 'Storage connection successful');
    },
    onError: (err: any) => {
      notifyError(err?.message || 'Failed to test storage connection');
    },
  });

  const handleSave = () => {
    // F-10: explicit update flags — blank field means "keep the existing encrypted
    // credential".  No magic sentinel strings; the boolean is self-documenting and
    // cannot be accidentally triggered by a real credential value.
    saveMutation.mutate({
      provider,
      endpoint,
      bucket,
      public_base_url: publicBaseUrl || undefined,
      region: region || undefined,
      ...(accessKeyId
        ? { updateAccessKey: true, access_key_id: accessKeyId }
        : { updateAccessKey: false }),
      ...(secretAccessKey
        ? { updateSecretKey: true, secret_access_key: secretAccessKey }
        : { updateSecretKey: false }),
    });
  };

  const handleTest = () => {
    testMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <HStack spacing={2} className="items-center py-4 justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span className="text-sm text-muted-foreground">Loading storage settings...</span>
          </HStack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <HStack spacing={2} className="items-center">
          <HardDrive className="w-5 h-5 text-primary-600" />
          <VStack spacing={0} className="flex-1">
            <HStack spacing={2} className="items-center">
              <CardTitle>Storage Configuration</CardTitle>
              {settings?.configured ? (
                <Badge variant="success">Configured</Badge>
              ) : (
                <Badge variant="secondary">Not Configured</Badge>
              )}
            </HStack>
            <CardDescription>
              Configure S3-compatible object storage for file uploads, knowledge base documents, and other assets.
            </CardDescription>
          </VStack>
        </HStack>
      </CardHeader>

      <CardContent>
        <VStack spacing={4}>
          <Select
            label="Storage Provider"
            value={provider}
            onValueChange={setProvider}
            options={PROVIDER_OPTIONS}
            placeholder="Select a provider..."
          />

          {provider && (
            <>
              <Input
                label="Endpoint URL"
                placeholder={
                  provider === 'r2' ? 'https://account-id.r2.cloudflarestorage.com' :
                  provider === 'hetzner' ? 'https://nbg1.your-objectstorage.com' :
                  provider === 's3' ? 'https://s3.us-east-1.amazonaws.com' :
                  'https://minio.example.com:9000'
                }
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                hint={
                  provider === 'hetzner' ? 'Use the base endpoint without bucket name, e.g. https://nbg1.your-objectstorage.com' :
                  undefined
                }
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Access Key ID"
                  type="password"
                  placeholder={settings?.access_key_id ? `Current: ${settings.access_key_id}` : 'Enter access key...'}
                  value={accessKeyId}
                  onChange={(e) => setAccessKeyId(e.target.value)}
                  hint="Leave blank to keep the existing key"
                />
                <Input
                  label="Secret Access Key"
                  type="password"
                  placeholder={settings?.secret_access_key ? `Current: ${settings.secret_access_key}` : 'Enter secret key...'}
                  value={secretAccessKey}
                  onChange={(e) => setSecretAccessKey(e.target.value)}
                  hint="Leave blank to keep the existing key"
                />
              </div>

              <Input
                label="Bucket Name"
                placeholder="my-app-uploads"
                value={bucket}
                onChange={(e) => setBucket(e.target.value)}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Public Base URL"
                  placeholder="https://cdn.example.com"
                  value={publicBaseUrl}
                  onChange={(e) => setPublicBaseUrl(e.target.value)}
                  hint="Optional. Used for generating public URLs to assets."
                />
                <Input
                  label="Region"
                  placeholder="auto"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  hint="Optional. Defaults to 'auto' for R2."
                />
              </div>

              <HStack spacing={3} className="pt-2">
                <Button
                  onClick={handleSave}
                  isLoading={saveMutation.isPending}
                >
                  Save Storage Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={handleTest}
                  isLoading={testMutation.isPending}
                  disabled={!settings?.configured}
                  leftIcon={<PlugZap className="w-4 h-4" />}
                >
                  Test Connection
                </Button>
              </HStack>
            </>
          )}
        </VStack>
      </CardContent>
    </Card>
  );
}
